/**
 * 동의 항목과 약관 본문 (시안 `_info3.html`).
 *
 * ★ 백엔드에 약관 테이블이 없다. `MC_*` 테이블은 회원·캠페인·포인트뿐이라
 *   약관을 담을 곳이 아직 없다. 나중에 테이블을 만들면 이 모듈의 타입
 *   (`TermsDocument`)을 그대로 응답 형태로 쓰면 된다 — 개정 이력을 남겨야 하는
 *   성격이라 버전·시행일 컬럼이 함께 필요하다.
 *
 * 동의 여부 자체는 이미 DB 에 있다 — `MC_USER.F_AGREE_MARKETING` /
 * `F_AGREE_AD` 와 각각의 `_DATE` (마이그레이션 `20260903_mc_user_agree.sql`).
 */

import { INFO_STEPS } from './policy.ts';

/** 동의 항목 종류. `MC_USER` 의 동의 컬럼과 1:1. */
export type ConsentKind = 'marketing' | 'ad';

/** 표 한 칸. `type` 이면 시안의 머리칸(`.type`) 스타일이 붙는다. */
export interface TermsCell {
  /** `\n` 은 줄바꿈으로 그린다 */
  text: string;
  type?: boolean;
}

export interface TermsTable {
  /** `<thead>` 에 전체 너비로 들어가는 제목. 없으면 생략한다. */
  heading?: string;
  /** `<colgroup>` 너비. 칸 수가 곧 열 수다. */
  columns: string[];
  rows: TermsCell[][];
}

export interface TermsDocument {
  kind: ConsentKind;
  /** 팝업 제목 */
  title: string;
  table: TermsTable;
  /** 표 아래 안내 문단 */
  notice: string;
}

/** 3단계 보상액 — 안내 문구가 이 값을 쓴다 */
const MARKETING_POINT = INFO_STEPS[2].amount;

const NOT_REQUIRED_NOTICE =
  `동의하지 않을 수 있으며 일반 서비스 이용에는 제한이 없습니다. ` +
  `다만 ${MARKETING_POINT}P는 지급되지 않습니다.`;

/** 약관 본문 */
export const TERMS: Record<ConsentKind, TermsDocument> = {
  marketing: {
    kind: 'marketing',
    title: '마케팅 정보 수신 동의',
    table: {
      heading: '마케팅 및 광고',
      columns: ['34%', '33%', '33%'],
      rows: [
        [
          { text: '수집·이용 목적', type: true },
          { text: '수집항목', type: true },
          { text: '보유기간', type: true },
        ],
        [
          { text: '마케팅 및 광고에 활용(신규 리워드 안내, 이벤트 안내 등)\n고객 통계 분석' },
          { text: '생년월일, 성별, 휴대전화번호, PUSH 토큰, 모바일 광고 식별자(IDFA/ADID)' },
          { text: '회원 탈퇴일로부터 1년' },
        ],
      ],
    },
    notice: `개인정보 수집·이용에 ${NOT_REQUIRED_NOTICE}`,
  },

  ad: {
    kind: 'ad',
    title: '광고성 정보 수신 동의',
    table: {
      columns: ['30%', '70%'],
      rows: [
        [{ text: '수신 목적', type: true }, { text: '이벤트·혜택 등 광고성 마케팅 정보 발송' }],
        [
          { text: '수신 채널', type: true },
          { text: '문자메시지(SMS/MMS), 카카오톡 메시지, 앱 푸시' },
        ],
        [
          { text: '철회 방법', type: true },
          { text: '마케팅 정보 수신 동의 설정에서 언제든 철회 가능' },
        ],
      ],
    },
    notice: `광고성 정보 수신에 ${NOT_REQUIRED_NOTICE}`,
  },
};

/** 3단계 체크박스 한 줄 */
export interface ConsentItem {
  kind: ConsentKind;
  /** 체크박스 옆 문구 */
  label: string;
}

/**
 * 3단계 동의 항목. 둘 다 **선택**이며 기본값은 해제다.
 * 사용자가 직접 켜야 하므로 어떤 경우에도 코드가 대신 켜지 않는다.
 */
export const CONSENT_ITEMS: ConsentItem[] = [
  { kind: 'marketing', label: '마케팅 활용을 위한 개인정보 수집·이용에 동의 (선택)' },
  { kind: 'ad', label: '광고성 정보 수신에 동의 (선택)' },
];

/** 동의 항목 아래 안내 문구 */
export const CONSENT_DESCRIPTION =
  '이벤트, 혜택, 신규 미션 등 마케팅 정보 수신에 동의해야 포인트 지급이 완료됩니다.';
